import { EmptyState, MenuList, MenuListItem } from '@nexus/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SessionInfo } from '../api/types';
import { formatDateTime, formatRelative } from '../i18n/format';
import { safePath } from '../lib/paths';
import { useModules } from '../modules/context';
import type { WebModule } from '../modules/define';
import { recentHits } from '../search/recent';
import { useSession } from '../session/session';
import { canRemindIn, myRemindersQuery } from '../reminders/api';
import { ReminderEntry } from '../reminders/reminder-entry';
import { approvalsQuery, canApproveIn } from '../workflows/api';
import { WhatsNewTile } from '../whats-new/whats-new-tile';
import type { DashboardTile } from './tiles';

const LIST_LENGTH = 5;

/** Records waiting for a decision of the person, from all active modules. */
function ApprovalsTile() {
  const { t, i18n } = useTranslation();
  const modules = useModules();
  const { data } = useSuspenseQuery(approvalsQuery);
  if (data.items.length === 0) {
    return (
      <EmptyState
        compact
        icon="clipboard-check"
        title={t('home.tiles.approvals.emptyTitle')}
        description={t('home.tiles.approvals.emptyDescription')}
      />
    );
  }
  const owner = (entity: string) =>
    modules.find((m) => (m.manifest.workflows ?? []).some((w) => w.entity === entity))?.manifest;
  return (
    <>
      <p className="px-4 pb-1 text-sm text-fg-muted">
        {t(data.more ? 'home.tiles.approvals.waitingMore' : 'home.tiles.approvals.waiting', {
          count: data.items.length,
        })}
      </p>
      <MenuList aria-label={t('home.tiles.approvals.title')}>
        {data.items.slice(0, LIST_LENGTH).map((item) => {
          const module = owner(item.entity);
          const at = new Date(item.enteredAt);
          return (
            <MenuListItem
              key={`${item.entity}:${item.recordId}`}
              icon={module?.icon}
              href={item.path}
              description={
                <>
                  {module && `${t(module.name)} · `}
                  <time dateTime={item.enteredAt} title={formatDateTime(at, i18n.language)}>
                    {formatRelative(at, i18n.language, t('common.relative.justNow'))}
                  </time>
                </>
              }
            >
              {item.title ?? t('approvals.untitled')}
            </MenuListItem>
          );
        })}
      </MenuList>
    </>
  );
}

/** The person's open reminders, earliest due first. */
function RemindersTile() {
  const { t } = useTranslation();
  const { data } = useSuspenseQuery(myRemindersQuery);
  if (data.items.length === 0) {
    return (
      <EmptyState
        compact
        icon="calendar-check"
        title={t('reminders.emptyTitle')}
        description={t('home.tiles.reminders.emptyDescription')}
      />
    );
  }
  return (
    <MenuList aria-label={t('home.tiles.reminders.title')}>
      {data.items.slice(0, LIST_LENGTH).map((item) => (
        <ReminderEntry key={item.id} item={item} />
      ))}
    </MenuList>
  );
}

/** Records opened recently, from the search or anywhere else. Only in this browser, cleared on
 * sign-out (search.md). */
function RecentTile() {
  const { t } = useTranslation();
  const session = useSession();
  const hits = useMemo(
    () => recentHits(session.account.id).filter((hit) => safePath(hit.path)),
    [session.account.id],
  );
  if (hits.length === 0) {
    return (
      <EmptyState
        compact
        icon="history"
        title={t('home.tiles.recent.emptyTitle')}
        description={t('home.tiles.recent.emptyDescription')}
      />
    );
  }
  return (
    <MenuList aria-label={t('home.tiles.recent.title')}>
      {hits.map((hit) => (
        <MenuListItem key={hit.path} icon={hit.icon} href={hit.path}>
          {hit.title}
        </MenuListItem>
      ))}
    </MenuList>
  );
}

interface KernelTile extends Omit<DashboardTile, 'accent' | 'source'> {
  /** Whether the tile makes sense for the person; without it every person sees the tile. */
  visible?: (modules: readonly WebModule[], session: SessionInfo) => boolean;
}

/** Tiles of the kernel, in their default order. They work without any module. */
export const KERNEL_TILES: readonly KernelTile[] = [
  {
    key: 'core.approvals',
    title: 'home.tiles.approvals.title',
    description: 'home.tiles.approvals.description',
    size: 'medium',
    path: '/for-me',
    icon: 'clipboard-check',
    flush: true,
    visible: canApproveIn,
    Content: ApprovalsTile,
  },
  {
    key: 'core.reminders',
    title: 'home.tiles.reminders.title',
    description: 'home.tiles.reminders.description',
    size: 'medium',
    path: '/for-me',
    icon: 'calendar-check',
    flush: true,
    visible: canRemindIn,
    Content: RemindersTile,
  },
  {
    key: 'core.whats-new',
    title: 'home.tiles.whatsNew.title',
    description: 'home.tiles.whatsNew.description',
    size: 'small',
    path: '/whats-new',
    icon: 'megaphone',
    flush: true,
    Content: WhatsNewTile,
  },
  {
    key: 'core.recent',
    title: 'home.tiles.recent.title',
    description: 'home.tiles.recent.description',
    size: 'small',
    icon: 'history',
    flush: true,
    Content: RecentTile,
  },
];
